import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { cn } from '@/lib/utils';
import {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  cancelLabel?: string;
  actionLabel?: string;
  actionLoadingLabel?: string;
  onAction?: () => void;
  isActionLoading?: boolean;
  isActionDisabled?: boolean;
  actionVariant?:
    | 'default'
    | 'destructive'
    | 'outline'
    | 'secondary'
    | 'ghost'
    | 'link';
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  /**
   * When true, the header doubles as a drag handle so the user can reposition
   * the modal — e.g. to uncover content sitting behind it. The modal recenters
   * each time it reopens.
   *
   * Repositioning is a pointer-only progressive enhancement (no keyboard path):
   * its only purpose is to visually uncover what's behind the modal, which has
   * no equivalent for non-visual users, and a focusable handle in the header
   * would steal Radix's initial focus from the first field. The modal stays
   * fully operable without it (centered, every control reachable), and the
   * handle exposes no interactive role to assistive tech.
   */
  draggable?: boolean;
}

const sizeValues = {
  sm: '28rem', // 448px
  md: '32rem', // 512px
  lg: '42rem', // 672px
  xl: '64rem', // 1024px
  '2xl': '72rem', // 1152px
};

// Keep at least this much of the modal on every viewport edge so it can't be
// dragged (or resized) fully off-screen and lost.
const VIEWPORT_MARGIN = 8;

const clamp = (value: number, min: number, max: number) =>
  min > max ? 0 : Math.min(Math.max(value, min), max);

// The drag offset is a plain pixel translate. Tailwind v4 centers the dialog
// with the standalone `translate` CSS property (`translate-x/y-[-50%]`), which
// the browser applies BEFORE `transform` — so a `transform: translate(px)`
// composes cleanly on top of the centering instead of stacking a second -50%
// (which would jump the modal half its size the moment a drag began).
const transformFor = (o: { x: number; y: number }) =>
  o.x || o.y ? `translate(${o.x}px, ${o.y}px)` : '';

// Drag bounds keep the whole modal within the viewport (minus the margin).
// Measured from the element's centered, offset-free top-left: rect already
// includes the live offset, so subtract it back out.
function measureBounds(el: HTMLElement, current: { x: number; y: number }) {
  const rect = el.getBoundingClientRect();
  const baseLeft = rect.left - current.x;
  const baseTop = rect.top - current.y;
  return {
    minX: VIEWPORT_MARGIN - baseLeft,
    maxX: window.innerWidth - VIEWPORT_MARGIN - rect.width - baseLeft,
    minY: VIEWPORT_MARGIN - baseTop,
    maxY: window.innerHeight - VIEWPORT_MARGIN - rect.height - baseTop,
  };
}

/**
 * Drag-to-reposition for the centered Radix dialog. The dialog centers itself
 * with the standalone `translate` property; we layer a pixel `transform` offset
 * on top of it (see `transformFor`) rather than fighting the centering.
 *
 * For smoothness the active drag is fully imperative: bounds are measured once
 * on pointer-down (no per-move `getBoundingClientRect`, so no layout thrashing)
 * and each move writes `transform` straight to the node (no per-move React
 * render of the Radix subtree). The offset is committed to state only on
 * release, so the rendered output stays correct across later re-renders. The
 * offset is always clamped to keep the modal on-screen — on every move and on
 * viewport resize.
 */
function useDraggableModal(isOpen: boolean, enabled: boolean) {
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  // Mirror of `offset` for the imperative pointer handlers, so a drag always
  // reads the latest position without re-subscribing window listeners.
  const offsetRef = useRef({ x: 0, y: 0 });
  const contentRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  // Detaches the active drag's window listeners — invoked on pointer-up, when a
  // new drag starts, and if the modal unmounts mid-drag.
  const teardownRef = useRef<(() => void) | null>(null);

  const setOffsetState = useCallback((next: { x: number; y: number }) => {
    offsetRef.current = next;
    setOffset(next);
  }, []);

  // Write the offset straight to the DOM, bypassing React. Used for every move
  // during an active drag so the modal tracks the cursor without re-rendering.
  const writeTransform = useCallback((o: { x: number; y: number }) => {
    if (contentRef.current)
      contentRef.current.style.transform = transformFor(o);
  }, []);

  // Recenter on open. useLayoutEffect runs before paint, so a modal reopened
  // after a previous drag never flashes at its old position.
  useLayoutEffect(() => {
    if (isOpen && enabled) setOffsetState({ x: 0, y: 0 });
  }, [isOpen, enabled, setOffsetState]);

  // If a forced re-render lands mid-drag, React would repaint the transform
  // from the (stale) committed offset; re-assert the live one after commit.
  useLayoutEffect(() => {
    if (draggingRef.current) writeTransform(offsetRef.current);
  });

  // Re-clamp the current position when the viewport resizes, so shrinking the
  // window can't leave a repositioned modal off-screen.
  useEffect(() => {
    if (!isOpen || !enabled) return;
    const onResize = () => {
      const el = contentRef.current;
      if (!el) return;
      const b = measureBounds(el, offsetRef.current);
      setOffsetState({
        x: clamp(offsetRef.current.x, b.minX, b.maxX),
        y: clamp(offsetRef.current.y, b.minY, b.maxY),
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isOpen, enabled, setOffsetState]);

  // Detach any in-flight drag listeners if we unmount mid-drag.
  useEffect(() => () => teardownRef.current?.(), []);

  const onDragStart = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      const el = contentRef.current;
      if (!enabled || !el) return;
      e.preventDefault();
      // A new pointer-down while a drag is somehow still live (e.g. multi-touch)
      // detaches the stale listeners before starting fresh.
      teardownRef.current?.();

      const bounds = measureBounds(el, offsetRef.current);
      const start = offsetRef.current;
      const pointerStartX = e.clientX;
      const pointerStartY = e.clientY;
      draggingRef.current = true;
      // The shared dialog carries a `duration-200` class; with no
      // `transition-property` set it resolves to `transition: all 200ms`, which
      // eases the transform and makes the modal trail the cursor. Suppress it
      // for the drag so the modal tracks 1:1; restored in teardown.
      el.style.transition = 'none';

      const handleMove = (moveEvent: PointerEvent) => {
        const next = {
          x: clamp(
            start.x + (moveEvent.clientX - pointerStartX),
            bounds.minX,
            bounds.maxX
          ),
          y: clamp(
            start.y + (moveEvent.clientY - pointerStartY),
            bounds.minY,
            bounds.maxY
          ),
        };
        offsetRef.current = next;
        writeTransform(next); // imperative — no React render per move
      };
      const teardown = () => {
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', handleUp);
        window.removeEventListener('pointercancel', handleUp);
        draggingRef.current = false;
        teardownRef.current = null;
        el.style.transition = ''; // restore the class-driven transition
      };
      const handleUp = () => {
        teardown();
        // Commit once so React owns the final transform on future renders.
        setOffset(offsetRef.current);
      };
      teardownRef.current = teardown;
      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleUp);
      window.addEventListener('pointercancel', handleUp);
    },
    [enabled, writeTransform]
  );

  return { offset, onDragStart, contentRef };
}

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  cancelLabel = 'Cancel',
  actionLabel,
  actionLoadingLabel = 'Loading...',
  onAction,
  isActionLoading = false,
  isActionDisabled = false,
  actionVariant = 'default',
  size = 'md',
  draggable = false,
}: ModalProps) {
  const { offset, onDragStart, contentRef } = useDraggableModal(
    isOpen,
    draggable
  );
  const isMoved = offset.x !== 0 || offset.y !== 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        ref={contentRef}
        className="p-0"
        style={{
          // min() keeps DialogContent's viewport gutter: a bare inline
          // maxWidth would override it and let wide sizes (xl/2xl) span
          // edge-to-edge on narrow windows. 100% (not 100vw — that includes
          // any scrollbar) of the fixed element's containing block, minus the
          // gutter defined on DialogContent itself. The 2rem fallback mirrors
          // DialogContent's [--dialog-gutter:2rem]; without it, a missing
          // variable would invalidate the whole min() and drop the size cap.
          maxWidth: `min(${sizeValues[size]}, calc(100% - var(--dialog-gutter, 2rem)))`,
          width: '100%',
          // Apply the pixel offset only once dragged, so the open/close zoom
          // animation is untouched while the modal sits centered. This composes
          // on top of Tailwind's `translate` centering (see `transformFor`).
          ...(isMoved ? { transform: transformFor(offset) } : {}),
        }}
      >
        <DialogHeader
          onPointerDown={draggable ? onDragStart : undefined}
          className={cn(
            'flex flex-row items-center justify-between p-6 pb-4 border-b border-border',
            draggable && 'cursor-move select-none'
          )}
        >
          <DialogTitle className="text-marketing-24">{title}</DialogTitle>
        </DialogHeader>

        {/* Content */}
        <div className="p-6 pt-4">{children}</div>

        {/* Footer */}
        {cancelLabel || actionLabel ? (
          <DialogFooter className="flex flex-row justify-end gap-3 p-6 pt-4 border-t border-border">
            {cancelLabel ? (
              <Button
                variant="outline"
                onClick={onClose}
                disabled={isActionLoading}
              >
                {cancelLabel}
              </Button>
            ) : null}
            {actionLabel && onAction ? (
              <Button
                variant={actionVariant}
                onClick={onAction}
                disabled={isActionLoading || isActionDisabled}
                className={
                  actionVariant === 'default'
                    ? 'bg-primary hover:bg-green-hover text-primary-foreground'
                    : ''
                }
              >
                {isActionLoading ? actionLoadingLabel : actionLabel}
              </Button>
            ) : null}
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
